import {SlButton} from '@shoelace-style/shoelace/shoelace';
import {notNull} from '../../../../../../../library/clxProductNet/assets/js/modules/common/assert';
import {Translator} from '../../../common/translator';

export class LanguageNavigation {
    private readonly languageSwitch: HTMLDivElement;
    private readonly dropdownHeader: HTMLDivElement;
    private readonly toggleButton: SlButton;
    private readonly dropdownIcon: HTMLElement;
    private readonly dropdownMenu: HTMLElement;
    private dropdownItems: HTMLElement[] = [];
    private readonly translationNavOpen: string;
    private readonly translationNavClose: string;
    private focusedIndex = 0;

    private onMenuOpenCallback?: () => void;

    constructor(selector: string) {
        this.languageSwitch = notNull(document.querySelector(selector) as HTMLDivElement);
        this.dropdownHeader = notNull(this.languageSwitch.querySelector<HTMLDivElement>('.dropdown-header'));
        this.toggleButton = notNull(this.languageSwitch.querySelector<SlButton>('sl-button.language-toggle'));
        this.dropdownIcon = notNull(this.toggleButton.querySelector<HTMLElement>('sl-icon'));
        this.dropdownMenu = notNull(this.languageSwitch.querySelector<HTMLElement>('.dropdown-menu'));

        const translator = new Translator();
        this.translationNavOpen = translator.translateText(this.toggleButton, 'nav-open');
        this.translationNavClose = translator.translateText(this.toggleButton, 'nav-close');

        this.dropdownItems = Array.from(this.dropdownMenu.querySelectorAll<HTMLElement>('li a'));

        this.dropdownItems.forEach((item, index) => {
            item.addEventListener('focus', () => {
                this.focusedIndex = index;
            });
        });

        this.initLanguageDropdown();
        this.initKeyboardCloseBehavior();
        this.initGlobalClickClose();
    }

    private initLanguageDropdown() {
        this.dropdownHeader.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.toggleDropdown();
        });

        this.languageSwitch.addEventListener('focusout', (event) => {
            const relatedTarget = (event.relatedTarget || document.activeElement) as HTMLElement;
            setTimeout(() => {
                if (!this.languageSwitch.contains(relatedTarget)) {
                    this.closeDropdown();
                }
            }, 0);
        });

        this.toggleButton.addEventListener('keydown', (event: KeyboardEvent) => {
            if ((event.key === 'ArrowDown' || event.key === 'Down') && this.dropdownMenu.classList.contains('open')) {
                event.preventDefault();
                this.focusedIndex = 0;
                this.focusItem(this.focusedIndex);
            }
        });

        this.dropdownMenu.addEventListener('keydown', (event: KeyboardEvent) => {
            const isDropdownFocused = this.dropdownMenu.contains(document.activeElement);

            if (event.key === 'ArrowDown' || event.key === 'Down') {
                event.preventDefault();
                this.focusedIndex = (this.focusedIndex + 1) % this.dropdownItems.length;
                this.focusItem(this.focusedIndex);
            } else if (event.key === 'ArrowUp' || event.key === 'Up') {
                event.preventDefault();
                if (this.focusedIndex === 0) {
                    this.closeDropdown();
                    setTimeout(() => this.toggleButton.focus(), 0);
                } else {
                    this.focusedIndex = (this.focusedIndex - 1 + this.dropdownItems.length) % this.dropdownItems.length;
                    this.focusItem(this.focusedIndex);
                }
            } else if (event.key === 'Tab' && event.shiftKey && isDropdownFocused) {
                event.preventDefault();
                this.closeDropdown();
                setTimeout(() => this.toggleButton.focus(), 0);
            } else if (event.key === 'Enter') {
                event.preventDefault();
                this.dropdownItems[this.focusedIndex].click();
            } else if (event.key === 'Escape' && this.isDropdownOpen()) {
                event.preventDefault();
                this.closeDropdown();
                this.toggleButton.focus();
            }
        });
    }

    private initKeyboardCloseBehavior(): void {
        document.addEventListener('keydown', (event: KeyboardEvent) => {
            if (event.key === 'Escape' && this.isDropdownOpen()) {
                event.preventDefault();
                this.closeDropdown();
                this.toggleButton.focus();
            }
        });
    }

    private initGlobalClickClose(): void {
        document.addEventListener('click', (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            const isClickInside = this.languageSwitch.contains(target);
            if (!isClickInside && this.isDropdownOpen()) {
                this.closeDropdown();
            }
        });
    }

    private isDropdownOpen(): boolean {
        return this.languageSwitch.classList.contains('active') && this.dropdownMenu.classList.contains('open');
    }

    private focusItem(index: number) {
        if (index >= 0 && index < this.dropdownItems.length) {
            this.dropdownItems[index].focus();
        }
    }

    private toggleDropdown() {
        const isActive = this.languageSwitch.classList.toggle('active');
        this.dropdownMenu.classList.toggle('open', isActive);
        this.toggleButton.setAttribute('aria-expanded', String(isActive));
        this.toggleButton.setAttribute('aria-label', isActive ? this.translationNavClose : this.translationNavOpen);
        this.dropdownIcon.setAttribute('name', isActive ? 'chevron-up' : 'chevron-down');

        if (isActive) {
            this.focusedIndex = 0;
            if (this.onMenuOpenCallback) {
                this.onMenuOpenCallback();
            }
        }
    }

    public closeDropdown() {
        this.languageSwitch.classList.remove('active');
        this.dropdownMenu.classList.remove('open');
        this.toggleButton.setAttribute('aria-expanded', 'false');
        this.toggleButton.setAttribute('aria-label', this.translationNavOpen);
        this.dropdownIcon.setAttribute('name', 'chevron-down');
    }

    public setOnMenuOpenCallback(callback: () => void): void {
        this.onMenuOpenCallback = callback;
    }
}
